(() => {
  const roots = document.querySelectorAll('[data-ccs-sheet-builder]');
  if (!roots.length) return;

  const PROCESS_MAX = 1000;
  const PROCESS_PAD = 54;
  const PRINT_DPI = 300;
  const MM_PER_INCH = 25.4;

  roots.forEach(initSheetBuilder);

  function initSheetBuilder(root) {
    const $ = selector => root.querySelector(selector);
    const $$ = selector => Array.from(root.querySelectorAll(selector));

    const el = {
      canvas: $('[data-sheet-canvas]'),
      emptyUpload: $('[data-empty-upload]'),
      input: $('[data-design-input]'),
      uploadError: $('[data-upload-error]'),
      designList: $('[data-design-list]'),
      designLimit: $('[data-design-limit]'),
      editor: $('[data-design-editor]'),
      orderControls: $('[data-order-controls]'),
      transparencyWarning: $('[data-transparency-warning]'),
      removeDesign: $('[data-remove-design]'),
      shapeButtons: $$('[data-shape]'),
      sizeRange: $('[data-size-range]'),
      sizeValue: $('[data-size-value]'),
      sizePresets: $$('[data-size-preset]'),
      copyCount: $('[data-copy-count]'),
      cleanBackground: $('[data-clean-background]'),
      borderEnabled: $('[data-border-enabled]'),
      borderControls: $('[data-border-controls]'),
      borderWidth: $('[data-border-width]'),
      borderValue: $('[data-border-value]'),
      colourButtons: $$('[data-border-colour]'),
      customColour: $('[data-custom-colour]'),
      positionControls: $('[data-position-controls]'),
      artworkZoom: $('[data-artwork-zoom]'),
      zoomValue: $('[data-zoom-value]'),
      offsetX: $('[data-offset-x]'),
      offsetY: $('[data-offset-y]'),
      smoothing: $('[data-smoothing]'),
      smoothingValue: $('[data-smoothing-value]'),
      qualityBox: $('[data-quality-box]'),
      qualityStatus: $('[data-quality-status]'),
      qualityDetail: $('[data-quality-detail]'),
      packingStatus: $('[data-packing-status]'),
      placedCount: $('[data-placed-count]'),
      proof: $('[data-download-proof]'),
      svg: $('[data-download-svg]'),
      sheetQuantityButtons: $$('[data-sheet-quantity]'),
      livePrice: $('[data-live-price]'),
      variantStatus: $('[data-variant-status]'),
      approval: $('[data-artwork-approval]'),
      add: $('[data-add-to-cart]'),
      cartStatus: $('[data-cart-status]'),
      variantCatalog: $('[data-variant-catalog]')
    };

    const state = {
      sheetWidth: number(root.dataset.sheetWidth, 210),
      sheetHeight: number(root.dataset.sheetHeight, 297),
      margin: number(root.dataset.sheetMargin, 5),
      gap: number(root.dataset.sheetGap, 5),
      maxDesigns: number(root.dataset.maxDesigns, 1),
      minSize: number(root.dataset.minSize, 40),
      maxSize: number(root.dataset.maxSize, 150),
      designs: [],
      selectedId: null,
      placements: [],
      overflow: 0,
      sheetQuantity: number((el.sheetQuantityButtons[0] || {}).dataset?.sheetQuantity, 1),
      selectedVariant: null,
      variantCatalog: null,
      renderTimer: 0,
      autoFillTimer: 0,
      busy: false
    };
    state.maxSize = Math.max(state.minSize, state.maxSize);

    try {
      state.variantCatalog = el.variantCatalog ? JSON.parse(el.variantCatalog.textContent) : null;
    } catch (error) {
      console.error('Sticker Sheet Builder variant data could not be read.', error);
    }

    bindEvents();
    updateUi();
    resizeAndRender();
    updateVariant();

    function bindEvents() {
      el.emptyUpload?.addEventListener('click', () => el.input.click());
      el.input?.addEventListener('change', handleUploads);
      el.removeDesign?.addEventListener('click', removeSelectedDesign);

      el.shapeButtons.forEach(button => button.addEventListener('click', () => {
        const design = selectedDesign();
        if (!design) return;
        design.shape = button.dataset.shape;
        rebuildDesign(design);
        syncEditor();
        fillSheetEvenly();
      }));

      el.sizeRange?.addEventListener('input', () => {
        const design = selectedDesign();
        if (!design) return;
        design.sizeMm = clamp(number(el.sizeRange.value, state.minSize), state.minSize, state.maxSize);
        rebuildDesign(design);
        syncEditor();
        repackAndRender();
        scheduleAutoFill();
      });

      el.sizePresets.forEach(button => button.addEventListener('click', () => {
        const design = selectedDesign();
        if (!design) return;
        design.sizeMm = clamp(number(button.dataset.sizePreset, state.minSize), state.minSize, state.maxSize);
        rebuildDesign(design);
        syncEditor();
        fillSheetEvenly();
      }));

      el.copyCount?.addEventListener('change', () => {
        const design = selectedDesign();
        if (!design) return;
        design.copies = clamp(Math.round(number(el.copyCount.value, 1)), 1, 200);
        el.copyCount.value = String(design.copies);
        repackAndRender();
      });

      el.cleanBackground?.addEventListener('change', () => {
        const design = selectedDesign();
        if (!design) return;
        design.cleanBackground = el.cleanBackground.checked;
        rebuildDesign(design);
        fillSheetEvenly();
      });

      el.borderEnabled?.addEventListener('change', () => {
        const design = selectedDesign();
        if (!design) return;
        design.borderEnabled = el.borderEnabled.checked;
        rebuildDesign(design);
        syncEditor();
        fillSheetEvenly();
      });

      el.borderWidth?.addEventListener('input', () => {
        const design = selectedDesign();
        if (!design) return;
        design.borderMm = clamp(number(el.borderWidth.value, 2), 1, 5);
        rebuildDesign(design);
        syncEditor();
        repackAndRender();
        scheduleAutoFill();
      });

      el.colourButtons.forEach(button => button.addEventListener('click', () => {
        setBorderColour(button.dataset.borderColour);
      }));

      el.customColour?.addEventListener('input', () => setBorderColour(el.customColour.value));

      el.artworkZoom?.addEventListener('input', () => {
        const design = selectedDesign();
        if (!design) return;
        design.zoom = clamp(number(el.artworkZoom.value, 100) / 100, 0.6, 1.8);
        syncEditor();
        scheduleRender();
      });

      el.offsetX?.addEventListener('input', () => {
        const design = selectedDesign();
        if (!design) return;
        design.offsetX = clamp(number(el.offsetX.value, 0), -40, 40);
        scheduleRender();
      });

      el.offsetY?.addEventListener('input', () => {
        const design = selectedDesign();
        if (!design) return;
        design.offsetY = clamp(number(el.offsetY.value, 0), -40, 40);
        scheduleRender();
      });

      el.smoothing?.addEventListener('input', () => {
        const design = selectedDesign();
        if (!design) return;
        design.smoothing = clamp(Math.round(number(el.smoothing.value, 4)), 0, 10);
        rebuildDesign(design);
        syncEditor();
        repackAndRender();
        scheduleAutoFill();
      });

      el.proof?.addEventListener('click', downloadProof);
      el.svg?.addEventListener('click', downloadSvg);
      el.approval?.addEventListener('change', updateAddButton);
      el.add?.addEventListener('click', addToCart);

      el.sheetQuantityButtons.forEach(button => button.addEventListener('click', () => {
        state.sheetQuantity = Math.max(1, Math.round(number(button.dataset.sheetQuantity, 1)));
        el.sheetQuantityButtons.forEach(item => item.classList.toggle('is-active', item === button));
        updateVariant();
      }));

      window.addEventListener('resize', resizeAndRender);
    }

    async function handleUploads(event) {
      const incoming = Array.from(event.target.files || []);
      event.target.value = '';
      clearUploadError();
      if (!incoming.length) return;

      const remaining = state.maxDesigns - state.designs.length;
      if (remaining <= 0) {
        showUploadError('This sheet already has its design. Remove it before uploading a different one.');
        return;
      }

      const files = incoming.slice(0, remaining);
      if (incoming.length > remaining) {
        showUploadError(`Only ${remaining} more design${remaining === 1 ? '' : 's'} can be added. The extra files were not loaded.`);
      }

      setBusy(true);
      try {
        for (const file of files) {
          if (file.size > 10 * 1024 * 1024) {
            showUploadError(`${file.name} is larger than the 10 MB limit.`);
            continue;
          }
          if (!validImageFile(file)) {
            showUploadError(`${file.name} is not a supported PNG, JPG, WebP or SVG file.`);
            continue;
          }

          try {
            const [image, sourceDataUrl] = await Promise.all([fileToImage(file), fileToDataUrl(file)]);
            const design = createDesign(file, image, sourceDataUrl);
            rebuildDesign(design);
            state.designs.push(design);
            state.selectedId = design.id;
          } catch (error) {
            console.error(error);
            showUploadError(`${file.name} could not be opened. Try exporting it as a PNG.`);
          }
        }

        if (state.designs.length) fillSheetEvenly();
      } finally {
        setBusy(false);
        updateUi();
        repackAndRender();
      }
    }

    function createDesign(file, image, sourceDataUrl) {
      return {
        id: `design-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        file,
        image,
        sourceDataUrl,
        name: file.name,
        thumbnail: makeThumbnail(image),
        sourceWidth: image.naturalWidth || image.width,
        sourceHeight: image.naturalHeight || image.height,
        processed: document.createElement('canvas'),
        mask: null,
        baseContour: [],
        contour: [],
        clipContour: [],
        bounds: { minX: 0, minY: 0, maxX: 1, maxY: 1 },
        sizeMm: clamp(50, state.minSize, state.maxSize),
        cutWidthMm: 50,
        cutHeightMm: 50,
        shape: 'die-cut',
        copies: 1,
        cleanBackground: false,
        transparent: false,
        borderEnabled: true,
        borderMm: number(el.borderWidth?.value, 2),
        borderColour: '#ffffff',
        smoothing: Math.round(number(el.smoothing?.value, 4)),
        zoom: 1,
        offsetX: 0,
        offsetY: 0,
        dpi: 0
      };
    }

    function rebuildDesign(design) {
      if (!design?.image) return;
      const scale = Math.min(1, PROCESS_MAX / Math.max(design.sourceWidth, design.sourceHeight));
      const artworkW = Math.max(1, Math.round(design.sourceWidth * scale));
      const artworkH = Math.max(1, Math.round(design.sourceHeight * scale));
      const canvas = design.processed;
      canvas.width = artworkW + PROCESS_PAD * 2;
      canvas.height = artworkH + PROCESS_PAD * 2;
      design.artworkX = PROCESS_PAD;
      design.artworkY = PROCESS_PAD;
      design.artworkWidth = artworkW;
      design.artworkHeight = artworkH;

      const context = canvas.getContext('2d', { alpha: true, willReadFrequently: true });
      context.clearRect(0, 0, canvas.width, canvas.height);
      context.drawImage(design.image, PROCESS_PAD, PROCESS_PAD, artworkW, artworkH);
      const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
      design.transparent = detectTransparency(imageData, PROCESS_PAD, PROCESS_PAD, artworkW, artworkH);

      for (let i = 3; i < imageData.data.length; i += 4) {
        if (imageData.data[i] < 10) imageData.data[i] = 0;
      }

      if (design.cleanBackground) removeConnectedBackground(imageData, PROCESS_PAD, PROCESS_PAD, artworkW, artworkH);
      context.putImageData(imageData, 0, 0);
      design.mask = alphaMask(imageData, 18);
      buildDesignContour(design);
      updateDesignDpi(design);
    }

    function buildDesignContour(design) {
      const w = design.processed.width;
      const h = design.processed.height;
      const visible = maskBounds(design.mask, w, h) || {
        x: design.artworkX,
        y: design.artworkY,
        width: design.artworkWidth,
        height: design.artworkHeight
      };
      const visibleLongest = Math.max(1, visible.width, visible.height);
      const pxPerMm = visibleLongest / Math.max(1, design.sizeMm);
      const borderPx = design.borderEnabled ? design.borderMm * pxPerMm : 0;
      design.clipContour = [];

      if (design.shape !== 'die-cut') {
        design.clipContour = geometricContour(design.shape, visible, 0);
        design.baseContour = design.clipContour.slice();
        design.contour = geometricContour(design.shape, visible, borderPx);
      } else if (isOpaqueRectangle(design.mask, w, h, design.artworkX, design.artworkY, design.artworkWidth, design.artworkHeight)) {
        design.clipContour = roundedRectangle(
          design.artworkX,
          design.artworkY,
          design.artworkWidth,
          design.artworkHeight,
          Math.max(2, pxPerMm)
        );
        design.baseContour = design.clipContour.slice();
        design.contour = roundedRectangle(
          design.artworkX - borderPx,
          design.artworkY - borderPx,
          design.artworkWidth + borderPx * 2,
          design.artworkHeight + borderPx * 2,
          Math.max(2, pxPerMm + borderPx)
        );
      } else {
        let working = removeSmallComponents(design.mask, w, h, Math.max(8, Math.round(countMask(design.mask) * 0.0007)));
        const joinStepsMm = [1.5, 2.5, 4, 6, 8];
        for (const joinMm of joinStepsMm) {
          if (countComponents(working, w, h) <= 1) break;
          working = closeMask(working, w, h, Math.min(50, Math.max(1, Math.round(joinMm * pxPerMm / 2))));
        }

        const smoothingRadius = design.smoothing > 0
          ? Math.min(18, Math.max(1, Math.round((0.35 + design.smoothing * 0.16) * pxPerMm)))
          : 0;
        if (smoothingRadius) working = closeMask(working, w, h, smoothingRadius);
        let baseContour = traceLargestContour(working, w, h);
        if (!baseContour.length) baseContour = geometricContour('rectangle', visible, 0);
        baseContour = simplifyRDP(closeLoop(baseContour), Math.max(.5, .55 + design.smoothing * .2));
        baseContour = chaikin(baseContour, Math.min(3, Math.floor(design.smoothing / 4)));
        design.baseContour = closeLoop(baseContour);

        const outerMask = borderPx > 0
          ? dilateMask(working, w, h, Math.min(80, Math.max(1, Math.round(borderPx))))
          : working;
        let contour = traceLargestContour(outerMask, w, h);
        if (!contour.length) contour = geometricContour('rectangle', visible, borderPx);
        contour = simplifyRDP(closeLoop(contour), Math.max(.5, .55 + design.smoothing * .2));
        contour = chaikin(contour, Math.min(3, Math.floor(design.smoothing / 4)));
        design.contour = closeLoop(contour);
      }

      design.bounds = contourBounds(design.contour);
      const contourW = Math.max(1, design.bounds.maxX - design.bounds.minX);
      const contourH = Math.max(1, design.bounds.maxY - design.bounds.minY);
      const contourLongest = Math.max(contourW, contourH);
      design.mmPerPixel = design.sizeMm / contourLongest;
      design.cutWidthMm = contourW * design.mmPerPixel;
      design.cutHeightMm = contourH * design.mmPerPixel;
    }

    function updateDesignDpi(design) {
      const sourceLongest = Math.max(design.sourceWidth, design.sourceHeight);
      design.dpi = Math.round(sourceLongest / Math.max(.01, design.sizeMm / MM_PER_INCH));
    }

    function selectedDesign() {
      return state.designs.find(design => design.id === state.selectedId) || null;
    }

    function setBorderColour(colour) {
      const design = selectedDesign();
      if (!design) return;
      design.borderColour = normaliseHex(colour);
      syncEditor();
      scheduleRender();
    }

    function removeSelectedDesign() {
      const index = state.designs.findIndex(design => design.id === state.selectedId);
      if (index < 0) return;
      state.designs.splice(index, 1);
      state.selectedId = state.designs[Math.min(index, state.designs.length - 1)]?.id || null;
      if (state.designs.length) fillSheetEvenly();
      updateUi();
      repackAndRender();
    }

    function fillSheetEvenly() {
      clearTimeout(state.autoFillTimer);
      if (!state.designs.length) return;
      state.designs.forEach(design => { design.copies = 0; });
      const blocked = new Set();
      let total = 0;

      while (blocked.size < state.designs.length && total < 200) {
        let addedThisRound = false;
        for (const design of state.designs) {
          if (blocked.has(design.id)) continue;
          design.copies++;
          const result = packDesigns(state.designs);
          if (result.overflow > 0) {
            design.copies--;
            blocked.add(design.id);
          } else {
            total++;
            addedThisRound = true;
          }
        }
        if (!addedThisRound) break;
      }

      state.designs.forEach(design => { design.copies = Math.max(1, design.copies); });
      syncEditor();
      repackAndRender();
    }

    function scheduleAutoFill() {
      clearTimeout(state.autoFillTimer);
      state.autoFillTimer = setTimeout(fillSheetEvenly, 90);
    }

    function repackAndRender() {
      const result = packDesigns(state.designs);
      state.placements = result.placements;
      state.overflow = result.overflow;
      updatePackingStatus();
      updateVariant();
      updateAddButton();
      scheduleRender();
      renderDesignList();
    }

    function packDesigns(designs) {
      const items = [];
      designs.forEach(design => {
        for (let index = 0; index < design.copies; index++) {
          items.push({ design, copyIndex: index, width: design.cutWidthMm, height: design.cutHeightMm });
        }
      });

      const strategies = [
        (a, b) => Math.max(b.width, b.height) - Math.max(a.width, a.height),
        (a, b) => b.width * b.height - a.width * a.height,
        (a, b) => b.height - a.height,
        (a, b) => b.width - a.width
      ];

      let best = { placements: [], overflow: items.length };
      for (const strategy of strategies) {
        const attempt = shelfPack(items.slice().sort(strategy));
        if (
          attempt.overflow < best.overflow ||
          (attempt.overflow === best.overflow && attempt.usedHeight < (best.usedHeight || Infinity))
        ) best = attempt;
      }
      return centrePackedPlacements(best);
    }

    function centrePackedPlacements(result) {
      if (!result.placements.length) return result;

      const rows = new Map();
      result.placements.forEach(placement => {
        const key = placement.y.toFixed(4);
        if (!rows.has(key)) rows.set(key, []);
        rows.get(key).push(placement);
      });

      const minY = Math.min(...result.placements.map(placement => placement.y));
      const maxY = Math.max(...result.placements.map(placement => placement.y + placement.height));
      const verticalOffset = (state.sheetHeight - (maxY - minY)) / 2 - minY;
      const centred = [];

      rows.forEach(row => {
        const minX = Math.min(...row.map(placement => placement.x));
        const maxX = Math.max(...row.map(placement => placement.x + placement.width));
        const horizontalOffset = (state.sheetWidth - (maxX - minX)) / 2 - minX;
        row.forEach(placement => centred.push({
          ...placement,
          x: placement.x + horizontalOffset,
          y: placement.y + verticalOffset
        }));
      });

      return { ...result, placements: centred };
    }

    function shelfPack(items) {
      const right = state.sheetWidth - state.margin;
      const bottom = state.sheetHeight - state.margin;
      const shelves = [];
      const placements = [];
      let nextY = state.margin;

      for (const item of items) {
        const orientations = [{ width: item.width, height: item.height }];

        let choice = null;
        for (let shelfIndex = 0; shelfIndex < shelves.length; shelfIndex++) {
          const shelf = shelves[shelfIndex];
          for (const orientation of orientations) {
            if (orientation.height > shelf.height + .001) continue;
            if (shelf.x + orientation.width > right + .001) continue;
            const waste = (right - shelf.x - orientation.width) + (shelf.height - orientation.height) * 1.8;
            if (!choice || waste < choice.waste) choice = { shelf, orientation, waste };
          }
        }

        if (!choice) {
          const fitting = orientations
            .filter(orientation => state.margin + orientation.width <= right + .001 && nextY + orientation.height <= bottom + .001)
            .sort((a, b) => a.height - b.height || b.width - a.width)[0];
          if (!fitting) continue;
          const shelf = { x: state.margin, y: nextY, height: fitting.height };
          shelves.push(shelf);
          nextY += fitting.height + state.gap;
          choice = { shelf, orientation: fitting };
        }

        const placement = {
          design: item.design,
          copyIndex: item.copyIndex,
          x: choice.shelf.x,
          y: choice.shelf.y,
          width: choice.orientation.width,
          height: choice.orientation.height
        };
        placements.push(placement);
        choice.shelf.x += choice.orientation.width + state.gap;
      }

      return {
        placements,
        overflow: Math.max(0, items.length - placements.length),
        usedHeight: shelves.length ? Math.max(...shelves.map(shelf => shelf.y + shelf.height)) : 0
      };
    }

    function resizeAndRender() {
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      const stage = el.canvas?.parentElement;
      if (!el.canvas || !stage) return;
      const compactMobile = window.matchMedia('(max-width: 749px)').matches;
      const maxWidth = Math.max(compactMobile ? 140 : 220, stage.clientWidth - (compactMobile ? 12 : 36));
      const maxHeight = compactMobile
        ? Math.max(190, Math.min(270, window.innerHeight * .3))
        : Math.max(360, Math.min(680, window.innerHeight * .72));
      const cssScale = Math.min(maxWidth / state.sheetWidth, maxHeight / state.sheetHeight);
      const cssW = Math.round(state.sheetWidth * cssScale);
      const cssH = Math.round(state.sheetHeight * cssScale);
      el.canvas.style.width = `${cssW}px`;
      el.canvas.style.height = `${cssH}px`;
      el.canvas.width = Math.max(1, Math.round(cssW * dpr));
      el.canvas.height = Math.max(1, Math.round(cssH * dpr));
      scheduleRender();
    }

    function scheduleRender() {
      clearTimeout(state.renderTimer);
      state.renderTimer = setTimeout(renderPreview, 25);
    }

    function renderPreview() {
      if (!el.canvas) return;
      const context = el.canvas.getContext('2d');
      renderSheet(context, el.canvas.width, el.canvas.height, true, true);
    }

    function renderSheet(context, width, height, includeCutlines, includeGuides) {
      context.clearRect(0, 0, width, height);
      context.fillStyle = '#ffffff';
      context.fillRect(0, 0, width, height);
      const scale = Math.min(width / state.sheetWidth, height / state.sheetHeight);

      if (includeGuides) {
        context.save();
        context.strokeStyle = '#c8c8cd';
        context.setLineDash([4 * scale, 3 * scale]);
        context.lineWidth = Math.max(1, .35 * scale);
        context.strokeRect(state.margin * scale, state.margin * scale, (state.sheetWidth - state.margin * 2) * scale, (state.sheetHeight - state.margin * 2) * scale);
        context.restore();
      }

      state.placements.forEach(placement => drawPlacement(context, placement, scale, includeCutlines));
    }

    function drawPlacement(context, placement, sheetScale, includeCutline) {
      const design = placement.design;
      const bounds = design.bounds;
      const sourceW = Math.max(1, bounds.maxX - bounds.minX);
      const sourceH = Math.max(1, bounds.maxY - bounds.minY);
      const originalWidthPx = design.cutWidthMm * sheetScale;
      const originalHeightPx = design.cutHeightMm * sheetScale;
      const scaleX = originalWidthPx / sourceW;
      const scaleY = originalHeightPx / sourceH;

      context.save();
      context.translate(placement.x * sheetScale, placement.y * sheetScale);
      context.scale(scaleX, scaleY);
      context.translate(-bounds.minX, -bounds.minY);

      fillContour(context, design.contour, '#ffffff');
      if (design.borderEnabled) {
        fillContour(context, design.contour, design.borderColour);
        fillContour(context, design.baseContour, '#ffffff');
      }

      context.save();
      if (design.clipContour.length) {
        pathContour(context, design.clipContour);
        context.clip();
      }
      applyArtworkTransform(context, design);
      context.drawImage(design.processed, 0, 0);
      context.restore();

      if (includeCutline) {
        context.save();
        pathContour(context, design.contour);
        context.setLineDash([5 / scaleX, 3.5 / scaleX]);
        context.lineWidth = Math.max(.7, 1.6 / scaleX);
        context.strokeStyle = '#f74302';
        context.lineJoin = 'round';
        context.lineCap = 'round';
        context.stroke();
        context.restore();
      }
      context.restore();
    }

    function applyArtworkTransform(context, design) {
      if (!design.clipContour.length) return;
      const clipBounds = contourBounds(design.clipContour);
      const centreX = (clipBounds.minX + clipBounds.maxX) / 2;
      const centreY = (clipBounds.minY + clipBounds.maxY) / 2;
      const offsetX = design.offsetX / 100 * (clipBounds.maxX - clipBounds.minX);
      const offsetY = design.offsetY / 100 * (clipBounds.maxY - clipBounds.minY);
      context.translate(centreX + offsetX, centreY + offsetY);
      context.scale(design.zoom, design.zoom);
      context.translate(-centreX, -centreY);
    }

    function updateUi() {
      const hasDesigns = state.designs.length > 0;
      if (el.emptyUpload) el.emptyUpload.hidden = hasDesigns;
      if (el.editor) el.editor.hidden = !hasDesigns;
      if (el.orderControls) el.orderControls.hidden = !hasDesigns;
      if (el.designLimit) el.designLimit.textContent = `${state.designs.length} / ${state.maxDesigns}`;
      if (el.input) el.input.disabled = state.designs.length >= state.maxDesigns;
      renderDesignList();
      syncEditor();
      updatePackingStatus();
      updateVariant();
    }

    function renderDesignList() {
      if (!el.designList) return;
      el.designList.innerHTML = '';
      state.designs.forEach((design, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = `ccs-sheet-builder__design-card${design.id === state.selectedId ? ' is-active' : ''}`;
        button.innerHTML = `<img alt=""><span>${escapeHtml(design.name)}</span><small>${design.copies} cop${design.copies === 1 ? 'y' : 'ies'}</small>`;
        button.querySelector('img').src = design.thumbnail;
        button.querySelector('img').alt = `Design ${index + 1}`;
        button.addEventListener('click', () => {
          state.selectedId = design.id;
          renderDesignList();
          syncEditor();
        });
        el.designList.appendChild(button);
      });
    }

    function syncEditor() {
      const design = selectedDesign();
      if (!design) return;
      el.shapeButtons.forEach(button => button.classList.toggle('is-active', button.dataset.shape === design.shape));
      if (el.sizeRange) el.sizeRange.value = String(design.sizeMm);
      if (el.sizeValue) el.sizeValue.textContent = trimNumber(design.sizeMm);
      el.sizePresets.forEach(button => button.classList.toggle('is-active', Math.abs(number(button.dataset.sizePreset, 0) - design.sizeMm) < .01));
      if (el.copyCount) el.copyCount.value = String(design.copies);
      if (el.cleanBackground) el.cleanBackground.checked = design.cleanBackground;
      if (el.borderEnabled) el.borderEnabled.checked = design.borderEnabled;
      if (el.borderControls) el.borderControls.hidden = !design.borderEnabled;
      if (el.borderWidth) el.borderWidth.value = String(design.borderMm);
      if (el.borderValue) el.borderValue.textContent = trimNumber(design.borderMm);
      if (el.customColour) el.customColour.value = design.borderColour;
      el.colourButtons.forEach(button => button.classList.toggle('is-active', normaliseHex(button.dataset.borderColour) === design.borderColour));
      const geometric = design.shape !== 'die-cut';
      if (el.positionControls) el.positionControls.hidden = !geometric;
      if (el.artworkZoom) el.artworkZoom.value = String(Math.round(design.zoom * 100));
      if (el.zoomValue) el.zoomValue.textContent = String(Math.round(design.zoom * 100));
      if (el.offsetX) el.offsetX.value = String(design.offsetX);
      if (el.offsetY) el.offsetY.value = String(design.offsetY);
      if (el.smoothing) el.smoothing.value = String(design.smoothing);
      if (el.smoothingValue) el.smoothingValue.textContent = String(design.smoothing);
      if (el.transparencyWarning) el.transparencyWarning.hidden = !design.transparent;
      updateQuality(design);
    }

    function updateQuality(design) {
      if (!el.qualityBox) return;
      let level = 'good';
      let label = `${design.dpi} DPI · Good`;
      if (design.dpi < 150) {
        level = 'low';
        label = `${design.dpi} DPI · Low quality`;
      } else if (design.dpi < 300) {
        level = 'ok';
        label = `${design.dpi} DPI · Usable`;
      }
      el.qualityBox.dataset.level = level;
      el.qualityStatus.textContent = label;
      el.qualityDetail.textContent = `${trimNumber(design.cutWidthMm)} × ${trimNumber(design.cutHeightMm)} mm finished cut size`;
    }

    function updatePackingStatus() {
      const requested = state.designs.reduce((sum, design) => sum + design.copies, 0);
      if (el.placedCount) el.placedCount.textContent = String(state.placements.length);
      if (!el.packingStatus) return;
      el.packingStatus.classList.toggle('is-error', state.overflow > 0);
      if (!state.designs.length) {
        el.packingStatus.textContent = '';
      } else if (state.overflow > 0) {
        el.packingStatus.textContent = `${state.placements.length} of ${requested} stickers fit. Reduce a copy count or finished size; ${state.overflow} currently do not fit.`;
      } else {
        el.packingStatus.textContent = `${requested} sticker${requested === 1 ? '' : 's'} fit on this A4 sheet with a 5 mm gap between every cut line.`;
      }
    }

    function updateVariant() {
      const catalog = state.variantCatalog;
      if (!catalog?.variants?.length) {
        state.selectedVariant = null;
        if (el.variantStatus) el.variantStatus.textContent = '';
        updateAddButton();
        return;
      }

      const optionNames = (catalog.options || []).map(value => String(value).toLowerCase());
      const quantityIndex = optionNames.findIndex(value => /(quantity|qty|sheets|pack|amount)/i.test(value));
      const match = catalog.variants.find(variant => {
        if (!variant.available) return false;
        if (catalog.variants.length === 1 && variant.title === 'Default Title') return true;
        const values = variant.options || [];
        if (quantityIndex >= 0 && values[quantityIndex] !== undefined) {
          return optionHasNumber(values[quantityIndex], state.sheetQuantity);
        }
        return [variant.title, ...values].some(value => optionHasNumber(value, state.sheetQuantity));
      });
      state.selectedVariant = match || null;
      if (match) root.dataset.variantId = String(match.id);
      if (el.livePrice) el.livePrice.textContent = match ? formatMoney(match.price) : 'Unavailable';
      const totalStickerCount = state.placements.length * state.sheetQuantity;
      if (el.variantStatus) el.variantStatus.textContent = match
        ? `${state.sheetQuantity} A4 sheet${state.sheetQuantity === 1 ? '' : 's'} selected (Total sticker count: ${totalStickerCount})`
        : `No product variant matches ${state.sheetQuantity} A4 sheet${state.sheetQuantity === 1 ? '' : 's'}.`;
      updateAddButton();
    }

    function updateAddButton() {
      if (!el.add) return;
      el.add.disabled = !state.designs.length || state.overflow > 0 || !state.selectedVariant || !el.approval?.checked || state.busy;
    }

    async function downloadProof() {
      if (!state.designs.length) return;
      const canvas = document.createElement('canvas');
      const scale = 5;
      canvas.width = Math.round(state.sheetWidth * scale);
      canvas.height = Math.round(state.sheetHeight * scale);
      renderSheet(canvas.getContext('2d'), canvas.width, canvas.height, true, false);
      const blob = await canvasToBlob(canvas, 'image/png');
      downloadBlob(blob, 'custom-sticker-sheet-a4-proof.png');
    }

    async function downloadSvg() {
      if (!state.designs.length) return;
      const blob = buildSvgBlob();
      downloadBlob(blob, 'custom-sticker-sheet-a4-production.svg');
    }

    function buildSvgBlob() {
      const defs = [];
      const body = [];
      state.designs.forEach((design, index) => {
        const artworkHref = design.cleanBackground
          ? design.processed.toDataURL('image/png')
          : design.sourceDataUrl;
        const artworkX = design.cleanBackground ? 0 : design.artworkX;
        const artworkY = design.cleanBackground ? 0 : design.artworkY;
        const artworkWidth = design.cleanBackground ? design.processed.width : design.artworkWidth;
        const artworkHeight = design.cleanBackground ? design.processed.height : design.artworkHeight;

        defs.push(`<path id="sticker-outline-${index}" d="${svgPath(design.contour)}"/>`);
        defs.push(`<path id="sticker-base-${index}" d="${svgPath(design.baseContour)}"/>`);
        defs.push(`<image id="sticker-artwork-${index}" xlink:href="${artworkHref}" x="${artworkX}" y="${artworkY}" width="${artworkWidth}" height="${artworkHeight}" preserveAspectRatio="none"/>`);
        if (design.clipContour.length) {
          defs.push(`<clipPath id="art-clip-${index}"><path d="${svgPath(design.clipContour)}"/></clipPath>`);
        }
      });

      state.placements.forEach((placement, placementIndex) => {
        const design = placement.design;
        const designIndex = state.designs.indexOf(design);
        const bounds = design.bounds;
        const mmScale = design.mmPerPixel;
        const transform = `translate(${trimNumber(placement.x)} ${trimNumber(placement.y)}) scale(${trimNumber(mmScale)}) translate(${trimNumber(-bounds.minX)} ${trimNumber(-bounds.minY)})`;

        const contourPath = svgPath(design.contour);
        const artworkTransform = artworkSvgTransform(design);
        const clip = design.clipContour.length ? ` clip-path="url(#art-clip-${designIndex})"` : '';
        body.push(`<g transform="${transform}">`);
        body.push(`<use href="#sticker-outline-${designIndex}" xlink:href="#sticker-outline-${designIndex}" fill="#ffffff"/>`);
        if (design.borderEnabled) {
          body.push(`<use href="#sticker-outline-${designIndex}" xlink:href="#sticker-outline-${designIndex}" fill="${design.borderColour}"/>`);
          body.push(`<use href="#sticker-base-${designIndex}" xlink:href="#sticker-base-${designIndex}" fill="#ffffff"/>`);
        }
        body.push(`<g${clip}${artworkTransform ? ` transform="${artworkTransform}"` : ''}><use href="#sticker-artwork-${designIndex}" xlink:href="#sticker-artwork-${designIndex}"/></g>`);
        body.push(`<path id="CutContour_${placementIndex + 1}" d="${contourPath}" fill="none" stroke="#ff00ff" stroke-width="${trimNumber(.25 / mmScale)}" stroke-linejoin="round"/>`);
        body.push('</g>');
      });

      const svg = `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="210mm" height="297mm" viewBox="0 0 210 297">
<defs>${defs.join('')}</defs>
${body.join('\n')}
</svg>`;
      return new Blob([svg], { type: 'image/svg+xml;charset=utf-8' });
    }

    function buildSingleDesignSvgBlob() {
      const design = state.designs[0];
      if (!design?.contour?.length) {
        throw new Error('No single-sticker cut path is available.');
      }

      const bounds = design.bounds;
      const mmScale = design.mmPerPixel;
      const widthMm = Math.max(0.01, design.cutWidthMm);
      const heightMm = Math.max(0.01, design.cutHeightMm);
      const outputPixelsPerSourcePixel = PRINT_DPI / MM_PER_INCH * mmScale;
      const rasterCanvas = document.createElement('canvas');
      rasterCanvas.width = Math.max(1, Math.round(widthMm * PRINT_DPI / MM_PER_INCH));
      rasterCanvas.height = Math.max(1, Math.round(heightMm * PRINT_DPI / MM_PER_INCH));
      const rasterContext = rasterCanvas.getContext('2d', { alpha: true });

      rasterContext.save();
      rasterContext.scale(outputPixelsPerSourcePixel, outputPixelsPerSourcePixel);
      rasterContext.translate(-bounds.minX, -bounds.minY);
      fillContour(rasterContext, design.contour, design.borderEnabled ? design.borderColour : '#ffffff');
      if (design.borderEnabled) fillContour(rasterContext, design.baseContour, '#ffffff');

      rasterContext.save();
      if (design.clipContour.length) {
        pathContour(rasterContext, design.clipContour);
        rasterContext.clip();
      }
      applyArtworkTransform(rasterContext, design);
      if (design.cleanBackground) {
        rasterContext.drawImage(design.processed, 0, 0);
      } else {
        rasterContext.drawImage(
          design.image,
          design.artworkX,
          design.artworkY,
          design.artworkWidth,
          design.artworkHeight
        );
      }
      rasterContext.restore();
      rasterContext.restore();

      const artworkData = rasterCanvas.toDataURL('image/png');
      const normaliseTransform = `scale(${trimNumber(mmScale)}) translate(${trimNumber(-bounds.minX)} ${trimNumber(-bounds.minY)})`;

      const svg = `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="${trimNumber(widthMm)}mm" height="${trimNumber(heightMm)}mm" viewBox="0 0 ${trimNumber(widthMm)} ${trimNumber(heightMm)}">
<image id="Artwork" x="0" y="0" width="${trimNumber(widthMm)}" height="${trimNumber(heightMm)}" preserveAspectRatio="none" xlink:href="${artworkData}"/>
<g transform="${normaliseTransform}">
  <path id="CutContour" d="${svgPath(design.contour)}" fill="none" stroke="#ff00ff" stroke-width="${trimNumber(.25 / mmScale)}" stroke-linejoin="round" stroke-linecap="round"/>
</g>
</svg>`;

      return new Blob([svg], { type: 'image/svg+xml;charset=utf-8' });
    }

    function artworkSvgTransform(design) {
      if (!design.clipContour.length) return '';
      const bounds = contourBounds(design.clipContour);
      const cx = (bounds.minX + bounds.maxX) / 2;
      const cy = (bounds.minY + bounds.maxY) / 2;
      const ox = design.offsetX / 100 * (bounds.maxX - bounds.minX);
      const oy = design.offsetY / 100 * (bounds.maxY - bounds.minY);
      return `translate(${trimNumber(cx + ox)} ${trimNumber(cy + oy)}) scale(${trimNumber(design.zoom)}) translate(${trimNumber(-cx)} ${trimNumber(-cy)})`;
    }

    async function addToCart() {
      if (el.add?.disabled) return;
      const variantId = state.selectedVariant?.id || root.dataset.variantId;
      if (!variantId) return;
      setBusy(true);
      setStatus('Preparing your A4 sticker sheet…');

      try {
        const proofCanvas = document.createElement('canvas');
        proofCanvas.width = 840;
        proofCanvas.height = 1188;
        renderSheet(proofCanvas.getContext('2d'), proofCanvas.width, proofCanvas.height, true, false);
        const proofBlob = await canvasToBlob(proofCanvas, 'image/jpeg', .88);
        const singleDesignSvgBlob = buildSingleDesignSvgBlob();
        setStatus('Uploading your artwork…');

        const form = new FormData();
        form.append('id', variantId);
        form.append('quantity', '1');
        form.append('properties[Stickers per sheet]', String(state.placements.length));
        form.append('properties[Total stickers]', String(state.placements.length * state.sheetQuantity));
        form.append('properties[Cut-line spacing]', '5 mm');
        form.append('properties[_Sheet settings]', buildSettingsSummary());
        state.designs.forEach((design, index) => {
          form.append(`properties[Artwork filename ${index + 1}]`, design.file.name);
          form.append('properties[_Original artwork]', design.file, design.file.name);
        });
        form.append('properties[_Generated proof]', new File([proofBlob], 'custom-sticker-sheet-a4-proof.jpg', { type: 'image/jpeg' }));
        form.append('properties[_Generated SVG cut file]', new File([singleDesignSvgBlob], 'custom-sticker-sheet-single-design-and-cut-path.svg', { type: 'image/svg+xml' }));

        const cartRoot = window.Shopify?.routes?.root || '/';
        const cartAddUrl = `${cartRoot.replace(/\/?$/, '/')}cart/add.js`;
        const response = await fetch(cartAddUrl, { method: 'POST', body: form, headers: { Accept: 'application/json' } });
        if (!response.ok) {
          const responseText = await response.text();
          let payload = {};
          try { payload = JSON.parse(responseText); } catch (error) { /* Shopify may return plain text or HTML. */ }
          const detail = payload.description || payload.message || responseText.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
          throw new Error(detail ? `Shopify could not add the sheet: ${detail.slice(0, 240)}` : `Shopify could not add the sheet (HTTP ${response.status}).`);
        }
        setStatus('Added to your basket.');
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        setTimeout(() => { window.location.href = `${cartRoot.replace(/\/?$/, '/')}cart`; }, 600);
      } catch (error) {
        console.error(error);
        setStatus(error.message || 'The sticker sheet could not be added.', true);
      } finally {
        setBusy(false);
      }
    }

    function buildSettingsSummary() {
      return state.designs.map((design, index) => [
        `D${index + 1}`,
        `${trimNumber(design.cutWidthMm)}x${trimNumber(design.cutHeightMm)}mm`,
        `${design.copies} copies`,
        design.shape,
        design.borderEnabled ? `${trimNumber(design.borderMm)}mm ${design.borderColour} border` : 'no printed border'
      ].join(' · ')).join(' | ');
    }

    function setBusy(busy) {
      state.busy = busy;
      root.classList.toggle('is-busy', busy);
      updateAddButton();
    }

    function showUploadError(message) {
      if (!el.uploadError) return;
      el.uploadError.textContent = message;
      el.uploadError.hidden = false;
    }

    function clearUploadError() {
      if (!el.uploadError) return;
      el.uploadError.textContent = '';
      el.uploadError.hidden = true;
    }

    function setStatus(message, error = false) {
      if (!el.cartStatus) return;
      el.cartStatus.textContent = message;
      el.cartStatus.style.color = error ? '#b42318' : '';
    }

    function formatMoney(cents) {
      return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(number(cents, 0) / 100);
    }
  }

  function validImageFile(file) {
    return /image\/(png|jpeg|webp|svg\+xml)/.test(file.type) || /\.(png|jpe?g|webp|svg)$/i.test(file.name);
  }

  function fileToImage(file) {
    return new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const image = new Image();
      image.onload = () => { URL.revokeObjectURL(url); resolve(image); };
      image.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Image could not be opened.')); };
      image.src = url;
    });
  }

  function fileToDataUrl(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ''));
      reader.onerror = () => reject(new Error('Artwork data could not be read.'));
      reader.readAsDataURL(file);
    });
  }

  function makeThumbnail(image) {
    const canvas = document.createElement('canvas');
    canvas.width = 100;
    canvas.height = 100;
    const context = canvas.getContext('2d');
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, 100, 100);
    const width = image.naturalWidth || image.width;
    const height = image.naturalHeight || image.height;
    const scale = Math.min(86 / width, 86 / height);
    context.drawImage(image, 50 - width * scale / 2, 50 - height * scale / 2, width * scale, height * scale);
    return canvas.toDataURL('image/png');
  }

  function detectTransparency(imageData, x, y, width, height) {
    let transparent = 0;
    let total = 0;
    const endX = Math.min(imageData.width, x + width);
    const endY = Math.min(imageData.height, y + height);
    for (let py = y; py < endY; py++) {
      for (let px = x; px < endX; px++) {
        total++;
        if (imageData.data[(py * imageData.width + px) * 4 + 3] < 250) transparent++;
      }
    }
    return transparent >= 10 && transparent / Math.max(1, total) >= .001;
  }

  function removeConnectedBackground(imageData, x, y, width, height) {
    const { data } = imageData;
    const w = imageData.width;
    const h = imageData.height;
    const left = clamp(Math.round(x), 0, w - 1);
    const top = clamp(Math.round(y), 0, h - 1);
    const right = clamp(Math.round(x + width - 1), 0, w - 1);
    const bottom = clamp(Math.round(y + height - 1), 0, h - 1);
    const samples = [];

    for (let px = left; px <= right; px += Math.max(1, Math.floor(width / 30))) {
      samples.push([px, top], [px, bottom]);
    }
    for (let py = top; py <= bottom; py += Math.max(1, Math.floor(height / 30))) {
      samples.push([left, py], [right, py]);
    }

    const colours = samples.map(([px, py]) => {
      const offset = (py * w + px) * 4;
      return { r: data[offset], g: data[offset + 1], b: data[offset + 2], a: data[offset + 3] };
    }).filter(sample => sample.a > 220);
    if (!colours.length) return;

    const target = colours.reduce((sum, colour) => ({ r: sum.r + colour.r, g: sum.g + colour.g, b: sum.b + colour.b }), { r: 0, g: 0, b: 0 });
    target.r /= colours.length;
    target.g /= colours.length;
    target.b /= colours.length;
    const average = (target.r + target.g + target.b) / 3;
    const spread = Math.max(target.r, target.g, target.b) - Math.min(target.r, target.g, target.b);
    if (!((average >= 175 || average <= 85) && spread <= 72)) return;

    const visited = new Uint8Array(w * h);
    const queue = new Int32Array(w * h);
    let head = 0;
    let tail = 0;
    const matches = index => {
      const offset = index * 4;
      if (data[offset + 3] < 12) return false;
      const dr = data[offset] - target.r;
      const dg = data[offset + 1] - target.g;
      const db = data[offset + 2] - target.b;
      const pixelSpread = Math.max(data[offset], data[offset + 1], data[offset + 2]) - Math.min(data[offset], data[offset + 1], data[offset + 2]);
      return Math.sqrt(dr * dr + dg * dg + db * db) <= 72 && pixelSpread <= 86;
    };
    const seed = (px, py) => {
      const index = py * w + px;
      if (!visited[index] && matches(index)) { visited[index] = 1; queue[tail++] = index; }
    };

    for (let px = left; px <= right; px++) { seed(px, top); seed(px, bottom); }
    for (let py = top; py <= bottom; py++) { seed(left, py); seed(right, py); }

    while (head < tail) {
      const index = queue[head++];
      const px = index % w;
      const py = Math.floor(index / w);
      data[index * 4 + 3] = 0;
      if (px > left) seed(px - 1, py);
      if (px < right) seed(px + 1, py);
      if (py > top) seed(px, py - 1);
      if (py < bottom) seed(px, py + 1);
    }
  }

  function alphaMask(imageData, threshold) {
    const mask = new Uint8Array(imageData.width * imageData.height);
    for (let i = 0; i < mask.length; i++) mask[i] = imageData.data[i * 4 + 3] > threshold ? 1 : 0;
    return mask;
  }

  function maskBounds(mask, w, h) {
    let minX = w, minY = h, maxX = -1, maxY = -1;
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        if (!mask[y * w + x]) continue;
        minX = Math.min(minX, x); minY = Math.min(minY, y); maxX = Math.max(maxX, x + 1); maxY = Math.max(maxY, y + 1);
      }
    }
    return maxX < minX ? null : { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
  }

  function isOpaqueRectangle(mask, w, h, x, y, width, height) {
    const left = Math.round(x), top = Math.round(y), right = Math.round(x + width), bottom = Math.round(y + height);
    let filled = 0, total = 0, edgeFilled = 0, edgeTotal = 0;
    for (let py = top; py < bottom; py++) {
      for (let px = left; px < right; px++) {
        const value = mask[py * w + px] ? 1 : 0;
        filled += value; total++;
        if (px < left + 2 || px >= right - 2 || py < top + 2 || py >= bottom - 2) { edgeFilled += value; edgeTotal++; }
      }
    }
    return total > 0 && filled / total > .985 && edgeFilled / Math.max(1, edgeTotal) > .97;
  }

  function geometricContour(shape, bounds, expansion) {
    const cx = bounds.x + bounds.width / 2;
    const cy = bounds.y + bounds.height / 2;
    if (shape === 'circle') {
      const radius = Math.max(bounds.width, bounds.height) / 2 + expansion;
      const points = [];
      for (let i = 0; i < 96; i++) {
        const angle = i / 96 * Math.PI * 2;
        points.push([cx + Math.cos(angle) * radius, cy + Math.sin(angle) * radius]);
      }
      return closeLoop(points);
    }
    if (shape === 'square') {
      const side = Math.max(bounds.width, bounds.height) + expansion * 2;
      return roundedRectangle(cx - side / 2, cy - side / 2, side, side, Math.max(2, side * .025));
    }
    return roundedRectangle(bounds.x - expansion, bounds.y - expansion, bounds.width + expansion * 2, bounds.height + expansion * 2, Math.max(2, Math.min(bounds.width, bounds.height) * .025 + expansion));
  }

  function roundedRectangle(x, y, width, height, radius) {
    const r = Math.min(Math.max(0, radius), width / 2, height / 2);
    const points = [];
    const corners = [
      [x + width - r, y + r, -Math.PI / 2, 0],
      [x + width - r, y + height - r, 0, Math.PI / 2],
      [x + r, y + height - r, Math.PI / 2, Math.PI],
      [x + r, y + r, Math.PI, Math.PI * 1.5]
    ];
    corners.forEach(([cx, cy, start, end]) => {
      for (let i = 0; i <= 10; i++) {
        const angle = start + (end - start) * i / 10;
        points.push([cx + Math.cos(angle) * r, cy + Math.sin(angle) * r]);
      }
    });
    return closeLoop(points);
  }

  function dilateMask(mask, w, h, radius) {
    if (radius <= 0) return mask.slice();
    const horizontal = new Uint8Array(mask.length);
    const output = new Uint8Array(mask.length);
    for (let y = 0; y < h; y++) {
      let count = 0;
      for (let x = 0; x <= radius && x < w; x++) count += mask[y * w + x];
      for (let x = 0; x < w; x++) {
        horizontal[y * w + x] = count > 0 ? 1 : 0;
        const remove = x - radius;
        const add = x + radius + 1;
        if (remove >= 0) count -= mask[y * w + remove];
        if (add < w) count += mask[y * w + add];
      }
    }
    for (let x = 0; x < w; x++) {
      let count = 0;
      for (let y = 0; y <= radius && y < h; y++) count += horizontal[y * w + x];
      for (let y = 0; y < h; y++) {
        output[y * w + x] = count > 0 ? 1 : 0;
        const remove = y - radius;
        const add = y + radius + 1;
        if (remove >= 0) count -= horizontal[remove * w + x];
        if (add < h) count += horizontal[add * w + x];
      }
    }
    return output;
  }

  function erodeMask(mask, w, h, radius) {
    if (radius <= 0) return mask.slice();
    const inverted = new Uint8Array(mask.length);
    for (let i = 0; i < mask.length; i++) inverted[i] = mask[i] ? 0 : 1;
    const expanded = dilateMask(inverted, w, h, radius);
    const output = new Uint8Array(mask.length);
    for (let i = 0; i < output.length; i++) output[i] = expanded[i] ? 0 : 1;
    return output;
  }

  function closeMask(mask, w, h, radius) {
    return erodeMask(dilateMask(mask, w, h, radius), w, h, radius);
  }

  function countMask(mask) {
    let count = 0;
    for (let i = 0; i < mask.length; i++) if (mask[i]) count++;
    return count;
  }

  function removeSmallComponents(mask, w, h, minimumArea) {
    const output = new Uint8Array(mask.length);
    walkComponents(mask, w, h, component => {
      if (component.length >= minimumArea) component.forEach(index => { output[index] = 1; });
    });
    return output;
  }

  function countComponents(mask, w, h) {
    let count = 0;
    walkComponents(mask, w, h, () => { count++; });
    return count;
  }

  function walkComponents(mask, w, h, callback) {
    const visited = new Uint8Array(mask.length);
    const queue = new Int32Array(mask.length);
    for (let start = 0; start < mask.length; start++) {
      if (!mask[start] || visited[start]) continue;
      let head = 0, tail = 0;
      const component = [];
      queue[tail++] = start;
      visited[start] = 1;
      while (head < tail) {
        const index = queue[head++];
        component.push(index);
        const x = index % w;
        const y = Math.floor(index / w);
        const neighbours = [x > 0 ? index - 1 : -1, x < w - 1 ? index + 1 : -1, y > 0 ? index - w : -1, y < h - 1 ? index + w : -1];
        neighbours.forEach(next => {
          if (next >= 0 && mask[next] && !visited[next]) { visited[next] = 1; queue[tail++] = next; }
        });
      }
      callback(component);
    }
  }

  function traceLargestContour(mask, w, h) {
    const adjacency = new Map();
    const edges = [];
    const key = (x, y) => `${x},${y}`;
    const add = (ax, ay, bx, by) => {
      const a = key(ax, ay);
      if (!adjacency.has(a)) adjacency.set(a, []);
      adjacency.get(a).push([bx, by]);
      edges.push(1);
    };
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        if (!mask[y * w + x]) continue;
        if (y === 0 || !mask[(y - 1) * w + x]) add(x, y, x + 1, y);
        if (x === w - 1 || !mask[y * w + x + 1]) add(x + 1, y, x + 1, y + 1);
        if (y === h - 1 || !mask[(y + 1) * w + x]) add(x + 1, y + 1, x, y + 1);
        if (x === 0 || !mask[y * w + x - 1]) add(x, y + 1, x, y);
      }
    }
    const visited = new Set();
    const loops = [];
    adjacency.forEach((nexts, startKey) => {
      nexts.forEach(next => {
        const initialEdge = `${startKey}>${key(next[0], next[1])}`;
        if (visited.has(initialEdge)) return;
        const [sx, sy] = startKey.split(',').map(Number);
        const loop = [[sx, sy]];
        let current = [sx, sy];
        let target = next;
        let guard = 0;
        while (guard++ < edges.length + 10) {
          const edgeKey = `${key(current[0], current[1])}>${key(target[0], target[1])}`;
          if (visited.has(edgeKey)) break;
          visited.add(edgeKey);
          loop.push(target);
          if (target[0] === sx && target[1] === sy) break;
          const candidates = adjacency.get(key(target[0], target[1])) || [];
          const nextTarget = candidates.find(point => !visited.has(`${key(target[0], target[1])}>${key(point[0], point[1])}`));
          if (!nextTarget) break;
          current = target;
          target = nextTarget;
        }
        if (loop.length > 4) loops.push(loop);
      });
    });
    loops.sort((a, b) => Math.abs(polygonArea(b)) - Math.abs(polygonArea(a)));
    return loops[0] || [];
  }

  function simplifyRDP(points, epsilon) {
    if (points.length < 3 || epsilon <= 0) return points;
    const closed = samePoint(points[0], points[points.length - 1]);
    const working = closed ? points.slice(0, -1) : points.slice();
    const recurse = list => {
      if (list.length < 3) return list;
      let maxDistance = 0;
      let index = 0;
      for (let i = 1; i < list.length - 1; i++) {
        const distance = perpendicularDistance(list[i], list[0], list[list.length - 1]);
        if (distance > maxDistance) { maxDistance = distance; index = i; }
      }
      if (maxDistance > epsilon) {
        const left = recurse(list.slice(0, index + 1));
        const right = recurse(list.slice(index));
        return left.slice(0, -1).concat(right);
      }
      return [list[0], list[list.length - 1]];
    };
    const reduced = recurse(working);
    return closed ? closeLoop(reduced) : reduced;
  }

  function chaikin(points, iterations) {
    if (!points.length) return [];
    let result = samePoint(points[0], points[points.length - 1]) ? points.slice(0, -1) : points.slice();
    for (let pass = 0; pass < iterations; pass++) {
      const next = [];
      for (let i = 0; i < result.length; i++) {
        const a = result[i];
        const b = result[(i + 1) % result.length];
        next.push([a[0] * .75 + b[0] * .25, a[1] * .75 + b[1] * .25]);
        next.push([a[0] * .25 + b[0] * .75, a[1] * .25 + b[1] * .75]);
      }
      result = next;
    }
    return closeLoop(result);
  }

  function pathContour(context, points) {
    if (!points.length) return;
    context.beginPath();
    context.moveTo(points[0][0], points[0][1]);
    for (let i = 1; i < points.length; i++) context.lineTo(points[i][0], points[i][1]);
    context.closePath();
  }

  function fillContour(context, points, colour) {
    pathContour(context, points);
    context.fillStyle = colour;
    context.fill();
  }

  function svgPath(points) {
    if (!points.length) return '';
    const loop = samePoint(points[0], points[points.length - 1]) ? points.slice(0, -1) : points;
    return `M ${trimNumber(loop[0][0])} ${trimNumber(loop[0][1])} ${loop.slice(1).map(point => `L ${trimNumber(point[0])} ${trimNumber(point[1])}`).join(' ')} Z`;
  }

  function contourBounds(points) {
    if (!points.length) return { minX: 0, minY: 0, maxX: 1, maxY: 1 };
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    points.forEach(([x, y]) => { minX = Math.min(minX, x); minY = Math.min(minY, y); maxX = Math.max(maxX, x); maxY = Math.max(maxY, y); });
    return { minX, minY, maxX, maxY };
  }

  function closeLoop(points) {
    if (!points.length) return [];
    const output = points.slice();
    if (!samePoint(output[0], output[output.length - 1])) output.push(output[0].slice());
    return output;
  }

  function samePoint(a, b) {
    return !!a && !!b && Math.abs(a[0] - b[0]) < .0001 && Math.abs(a[1] - b[1]) < .0001;
  }

  function polygonArea(points) {
    let area = 0;
    for (let i = 0, j = points.length - 1; i < points.length; j = i++) area += (points[j][0] + points[i][0]) * (points[j][1] - points[i][1]);
    return area / 2;
  }

  function perpendicularDistance(point, start, end) {
    const dx = end[0] - start[0];
    const dy = end[1] - start[1];
    if (!dx && !dy) return Math.hypot(point[0] - start[0], point[1] - start[1]);
    return Math.abs(dy * point[0] - dx * point[1] + end[0] * start[1] - end[1] * start[0]) / Math.hypot(dx, dy);
  }

  function optionHasNumber(value, target) {
    const numbers = String(value || '').match(/\d+(?:[.,]\d+)?/g) || [];
    return numbers.some(item => Math.abs(Number(item.replace(',', '.')) - Number(target)) < .001);
  }

  function canvasToBlob(canvas, type, quality = 1) {
    return new Promise((resolve, reject) => canvas.toBlob(blob => blob ? resolve(blob) : reject(new Error('The preview file could not be generated.')), type, quality));
  }

  function downloadBlob(blob, filename) {
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1200);
  }

  function number(value, fallback) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
  }

  function trimNumber(value) {
    return Number(Number(value).toFixed(3)).toString();
  }

  function normaliseHex(value) {
    const colour = String(value || '#ffffff').trim().toLowerCase();
    return /^#[0-9a-f]{6}$/.test(colour) ? colour : '#ffffff';
  }

  function escapeHtml(value) {
    return String(value).replace(/[&<>'"]/g, character => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' }[character]));
  }
})();
